export const ROLES = {
  SUPER_ADMIN: 1,
  SYSTEM_ADMIN: 2,
  PRACTICE_ADMIN: 3,
  PROVIDER: 4,
  CAREGIVER: 5,
  PATIENT: 6,
  SYSTEM_CAREGIVER: 7,
};

export function getRoleId(user) {
  return Number(user?.role_id);
}

export function isPracticeAdmin(user) {
  return getRoleId(user) === ROLES.PRACTICE_ADMIN;
}

export function getPracticeAdminHomePath(user) {
  if (!user?.practice_id) return '/dashboard';
  return `/practices/${user.practice_id}`;
}

export function getRoleDisplayName(roleId) {
  const roles = {
    1: 'Super Admin',
    2: 'Admin',
    3: 'Practice Admin',
    4: 'Provider',
    5: 'Caregiver',
    6: 'Patient',
    7: 'System CareGiver',
  };
  return roles[Number(roleId)] || 'User';
}

/** Roles Super Admin may assign from Settings → User Roles. */
export const ROLE_CHANGE_TARGETS = [
  { id: ROLES.PRACTICE_ADMIN, label: 'Practice Admin', group: 'Practice' },
  { id: ROLES.PROVIDER, label: 'Provider', group: 'Practice' },
  { id: ROLES.CAREGIVER, label: 'Caregiver', group: 'Practice' },
];

export function getStaffRoleLabel(roleId) {
  const found = ROLE_CHANGE_TARGETS.find((role) => role.id === Number(roleId));
  return found?.label || getRoleDisplayName(roleId);
}

/** Staff who see the patient Call button (not Super Admin, not Patient). */
export function canPlacePatientCall(user) {
  return [
    ROLES.SYSTEM_ADMIN,
    ROLES.PRACTICE_ADMIN,
    ROLES.PROVIDER,
    ROLES.CAREGIVER,
    ROLES.SYSTEM_CAREGIVER,
  ].includes(getRoleId(user));
}

/** Users scoped to a single practice — no global practices list in breadcrumbs. */
export function isSinglePracticeScopedUser(user) {
  return [ROLES.PRACTICE_ADMIN, ROLES.PROVIDER, ROLES.CAREGIVER, ROLES.SYSTEM_CAREGIVER].includes(getRoleId(user));
}

/** Provider: view-only on providers (no create, edit, or delete). */
export function canCreateProvider(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

export function canEditProvider(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

export function canDeleteProvider(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

/** Provider: view-only on practice admins (no create, edit, or delete). */
export function canCreateAdmin(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

export function canEditAdmin(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

export function canDeleteAdmin(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

/** Provider: may add/edit caregivers but not delete them. */
export function canDeleteCaregiver(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

/** Provider and caregiver: may add/edit patients but not delete them. */
export function canDeletePatient(user) {
  const roleId = getRoleId(user);
  return roleId !== ROLES.PROVIDER && roleId !== ROLES.CAREGIVER;
}

/** Super Admin, Practice Admin, and Provider may correct follow-up service time after save. */
export function canEditFollowUpTime(user) {
  const roleId = getRoleId(user);
  return [
    ROLES.SUPER_ADMIN,
    ROLES.PRACTICE_ADMIN,
    ROLES.PROVIDER,
  ].includes(roleId);
}

/** Provider: view-only on system caregivers (no create, edit, or delete). */
export function canCreateSystemCaregiver(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

export function canEditSystemCaregiver(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}

export function canDeleteSystemCaregiver(user) {
  return getRoleId(user) !== ROLES.PROVIDER;
}
